import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { usePendingWork } from '../contexts/PendingWorkContext';
import './PendingWorkBanner.css';

/**
 * แถบบอกทาง "งานรอดำเนินการ" สำหรับเจ้าหน้าที่ — โชว์ใต้ header ทุกหน้า
 *
 * โจทย์: กดลิงก์จาก Flex ใน LINE เข้ามาแล้วต้องรู้ทันทีว่าไปหน้าไหนต่อ
 * - แสดงเฉพาะเมื่อมีงานค้างที่ "ไม่ใช่หน้าที่กำลังเปิดอยู่" (อยู่หน้านั้นแล้วก็ไม่ต้องบอกซ้ำ)
 * - แต่ละ chip กดแล้วพาไปหน้านั้นเลย
 * - ย่อได้ (จำต่อแท็บ) แต่ถ้ามีงานเพิ่มขึ้นจะกางกลับมาเอง
 */

const COLLAPSE_KEY = 'imoney_pending_banner_collapsed';

function readCollapsed() {
    try {
        const raw = sessionStorage.getItem(COLLAPSE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return (parsed && typeof parsed.total === 'number') ? parsed : null;
    } catch (e) {
        return null;
    }
}

function isOnPath(pathname, target) {
    return pathname === target || pathname.startsWith(target + '/');
}

export default function PendingWorkBanner() {
    const { enabled, items, total, loaded } = usePendingWork();
    const location = useLocation();
    const [collapsedState, setCollapsedState] = useState(readCollapsed);

    // งานที่อยู่ "หน้าอื่น" — ถ้าอยู่หน้านั้นอยู่แล้วให้ตัดออก (ผู้ใช้เห็นรายการตรงหน้าอยู่แล้ว)
    const elsewhere = useMemo(
        () => items.filter(it => !isOnPath(location.pathname, it.path)),
        [items, location.pathname]
    );
    const hereCount = total - elsewhere.reduce((s, it) => s + it.count, 0);
    const elsewhereTotal = elsewhere.reduce((s, it) => s + it.count, 0);

    // มีงานเพิ่มขึ้นหลังจากที่ย่อไว้ -> กางกลับมาให้เห็น
    useEffect(() => {
        if (collapsedState && total > collapsedState.total) {
            setCollapsedState(null);
            try { sessionStorage.removeItem(COLLAPSE_KEY); } catch (e) { /* ignore */ }
        }
    }, [total, collapsedState]);

    if (!enabled || !loaded) return null;
    if (elsewhere.length === 0) return null;

    const collapse = () => {
        const next = { total, at: Date.now() };
        setCollapsedState(next);
        try { sessionStorage.setItem(COLLAPSE_KEY, JSON.stringify(next)); } catch (e) { /* ignore */ }
    };
    const expand = () => {
        setCollapsedState(null);
        try { sessionStorage.removeItem(COLLAPSE_KEY); } catch (e) { /* ignore */ }
    };

    if (collapsedState) {
        return (
            <button type="button" className="pwb pwb-collapsed" onClick={expand} title="กดเพื่อดูว่างานค้างอยู่หน้าไหน">
                <span className="pwb-bell" aria-hidden="true">
                    <BellIcon />
                </span>
                <span className="pwb-collapsed-text">
                    งานรอดำเนินการ <strong>{elsewhereTotal}</strong> รายการ ที่หน้าอื่น
                </span>
                <span className="pwb-collapsed-action">ดูรายการ</span>
            </button>
        );
    }

    return (
        <section className="pwb" role="status" aria-live="polite" aria-label="งานรอดำเนินการ">
            <div className="pwb-head">
                <span className="pwb-bell" aria-hidden="true">
                    <BellIcon />
                </span>
                <div className="pwb-title">
                    <strong>มีงานรอดำเนินการ {elsewhereTotal} รายการ</strong>
                    <span>
                        กดที่รายการเพื่อไปยังหน้านั้น
                        {hereCount > 0 ? ` (ไม่นับ ${hereCount} รายการที่อยู่ในหน้านี้)` : ''}
                    </span>
                </div>
                <button type="button" className="pwb-collapse" onClick={collapse} title="ย่อแถบนี้ (จะกางกลับเมื่อมีงานใหม่)">
                    ย่อ
                </button>
            </div>
            <div className="pwb-chips">
                {elsewhere.map(it => (
                    <Link key={it.key} to={it.path} className="pwb-chip" title={it.hint || it.label}>
                        <span className="pwb-chip-count">{it.count > 99 ? '99+' : it.count}</span>
                        <span className="pwb-chip-label">{it.label}</span>
                        <svg className="pwb-chip-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M9 5l7 7-7 7" />
                        </svg>
                    </Link>
                ))}
            </div>
        </section>
    );
}

function BellIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
    );
}
